/**
 * Closing a job out — issue #835, ADR 0273.
 *
 * Jonathon, 2026-10-06: "on the approved checklist, after the final check
 * complete there needs to be a button that says Send to Kelsey. when someone
 * clicks that button there needs to be a pop up that says, are all of the
 * trades correct (and it needs to show them what might not be), is the
 * estimate amount correct, are the work orders accurate and complete, are the
 * material orders accurate, are the other expenses accurate and complete."
 *
 * This file is everything that pop-up and Kelsey's email say. Both read the
 * same `closeoutReview`, so the rep and Kelsey are shown the same facts and the
 * same warnings, worded the same way.
 *
 * A warning never blocks Send. It is what the rep is being asked to look at
 * before they say the job is right; the five ticks are them saying it.
 */

import type { Balance } from "./balance.js";
import { QUESTION, amountSection, tradesSection } from "./closeoutChecks.js";
import {
  expensesSection,
  materialOrdersSection,
  workOrdersSection,
} from "./closeoutOrders.js";
import { computeLadder, type LadderInput } from "./ladder.js";
import type { ReceiptCustomer } from "./receiptCustomer.js";
import type {
  ChangeOrder,
  Estimate,
  EstimateItem,
  JobExpense,
  JobStage,
  MaterialOrder,
  WorkOrder,
} from "./types.js";

/* ------------------------------------------------------------ questions */

export type CloseoutKey =
  "trades" | "amount" | "work_orders" | "material_orders" | "expenses";

/** The five questions, in his order and close to his words. */
export const CLOSEOUT_QUESTIONS: readonly {
  key: CloseoutKey;
  question: string;
}[] = (
  ["trades", "amount", "work_orders", "material_orders", "expenses"] as const
).map((key) => ({ key, question: QUESTION[key] }));

export const CLOSEOUT_KEYS: readonly CloseoutKey[] = CLOSEOUT_QUESTIONS.map(
  (q) => q.key,
);

/** Send is offered once every question is ticked, and not before. */
export function readyToSend(ticked: ReadonlySet<string>): boolean {
  return CLOSEOUT_KEYS.every((key) => ticked.has(key));
}

/* ---------------------------------------------------------------- input */

/** One active estimate with everything the ladder reads, as fetched. */
export interface CloseoutOption extends Omit<
  LadderInput,
  "jobType" | "estimate" | "items" | "changeOrders" | "workOrders"
> {
  estimate: LadderInput["estimate"] &
    Pick<Estimate, "id"> &
    Partial<Pick<Estimate, "name" | "trade">>;
  items: readonly (LadderInput["items"][number] &
    Partial<Pick<EstimateItem, "trade" | "description">>)[];
  changeOrders: readonly (LadderInput["changeOrders"][number] &
    Partial<Pick<ChangeOrder, "id" | "trade" | "description">>)[];
  workOrders: readonly (LadderInput["workOrders"][number] &
    Pick<WorkOrder, "id" | "order_number" | "status"> &
    Partial<
      Pick<
        WorkOrder,
        | "trades"
        | "crew_id"
        | "crew"
        | "completed_at"
        | "payout_approved_at"
        | "payout_rejected_at"
        | "payout_held_at"
        | "payout_sent_at"
        | "payout_paid_at"
        | "payout_paid_manually_at"
        | "payout_amount"
      >
    >)[];
  materialOrders: readonly (LadderInput["materialOrders"][number] &
    Pick<MaterialOrder, "id" | "order_number" | "status"> &
    Partial<Pick<MaterialOrder, "trade" | "supplier" | "reconciled_at">>)[];
  expenses: readonly (LadderInput["expenses"][number] &
    Partial<
      Pick<
        JobExpense,
        "id" | "name" | "trade" | "payment_type" | "document_url"
      >
    >)[];
}

export type QuickbooksBillKind =
  "crew_payout" | "material_order" | "job_expense";

/** What QuickBooks has heard about this job, off the four queue tables. */
export interface CloseoutQuickbooks {
  invoice: {
    state: string;
    docNumber: string | null;
    amountCents: number | null;
    sentAmountCents: number | null;
    lastError: string | null;
  } | null;
  bills: readonly {
    sourceKind: QuickbooksBillKind;
    sourceId: string;
    state: string;
    amountCents: number | null;
    docNumber: string | null;
    lastError: string | null;
  }[];
  payments: readonly { amountCents: number; state: string }[];
  receipts: readonly {
    vendor: string | null;
    amountCents: number | null;
    txnDate: string | null;
  }[];
  /**
   * Every receipt still waiting in the receipts list, on no job yet. The
   * review keeps the ones that name this customer (`receiptNamesCustomer`,
   * issue #899). Absent reads as none.
   */
  waitingReceipts?: readonly {
    vendor: string | null;
    description: string | null;
    amountCents: number | null;
    txnDate: string | null;
  }[];
}

export interface CloseoutInput {
  job: {
    jobType?: string | null | undefined;
    /** Where the job is, for whose permit fee it pays (ADR 0289). */
    place?: LadderInput["place"];
    /** No work orders are coming (ADR 0102). */
    work_orders_internal?: boolean | null | undefined;
    /** No material orders are coming, one way or the other. */
    materials_internal?: boolean | null | undefined;
    material_by_crew?: boolean | null | undefined;
    /** Who the job is for, to find a waiting receipt that names them. */
    customer?: ReceiptCustomer | null | undefined;
  };
  checklist: readonly { phase: string; label: string; status: string }[];
  options: readonly CloseoutOption[];
  /** `jobBalance`, the one answer to "how much is left". Null if unknown. */
  balance: Balance | null;
  quickbooks: CloseoutQuickbooks;
  /** What each crew is recorded as doing, by crew id. */
  crewTrades: Readonly<Record<string, readonly string[]>>;
}

/* --------------------------------------------------------------- output */

export interface CloseoutSection {
  key: CloseoutKey;
  question: string;
  /** What the job says, a line each, for the person to check. */
  facts: string[];
  /** What might not be right. */
  warnings: string[];
}

export interface CloseoutReview {
  /** Above the five questions — QA Inspection not ticked, today. */
  general: string[];
  sections: CloseoutSection[];
  /** Every warning, general ones first, for the record and the board. */
  warnings: { key: CloseoutKey | "general"; text: string }[];
}

/* --------------------------------------------------------------- review */

const ticked = (
  checklist: CloseoutInput["checklist"],
  label: string,
): boolean | null => {
  const item = checklist.find(
    (i) => i.phase === "production" && i.label === label,
  );
  return item ? item.status === "done" : null;
};

/** Above the five questions: what is true of the job as a whole. */
function generalWarnings(input: CloseoutInput): string[] {
  const general: string[] = [];
  // Retail and insurance have a QA Inspection after Final Check; a repair
  // does not. Not ticked is a warning and never a block — Jonathon,
  // 2026-10-06, "After Final Check".
  if (ticked(input.checklist, "QA Inspection") === false)
    general.push("QA Inspection is not ticked yet.");
  if (input.options.length === 0)
    general.push("This job has no active estimate.");
  return general;
}

/**
 * Everything the pop-up shows and the email repeats.
 *
 * Pure, and reading only what it is handed: the screen fetches, this decides
 * what to say about it (CLAUDE.md rule 5). The five sections are worked out
 * in closeoutChecks.ts and closeoutOrders.ts.
 */
export function closeoutReview(input: CloseoutInput): CloseoutReview {
  const ladders = input.options.map((option) =>
    computeLadder({
      ...option,
      jobType: input.job.jobType,
      place: input.job.place,
    }),
  );
  const general = generalWarnings(input);
  const sections = [
    tradesSection(input),
    amountSection(input, ladders),
    workOrdersSection(input),
    materialOrdersSection(input),
    expensesSection(input, ladders),
  ];
  return {
    general,
    sections,
    warnings: [
      ...general.map((text) => ({ key: "general" as const, text })),
      ...sections.flatMap((s) =>
        s.warnings.map((text) => ({ key: s.key, text })),
      ),
    ],
  };
}

export { closeoutEmail, type CloseoutEmailInput } from "./closeoutEmail.js";

/* ---------------------------------------------------------------- board */

export type CloseoutColumn =
  "signed" | "in_production" | "work_complete" | "ready_to_close";

export const CLOSEOUT_COLUMNS: readonly {
  key: CloseoutColumn;
  title: string;
}[] = [
  { key: "signed", title: "Signed" },
  { key: "in_production", title: "In Production" },
  { key: "work_complete", title: "Work Complete" },
  { key: "ready_to_close", title: "Ready to Close" },
];

/**
 * Where a job sits on Kelsey's board, or null when it is not on it.
 *
 * Sold and not yet closed or lost. Sent outranks everything: once a rep has
 * sent it, it is hers whatever its stage says.
 */
export function closeoutColumn(
  job: { stage: JobStage; sends_at: string | null },
  sent: boolean,
): CloseoutColumn | null {
  if (job.stage === "lost" || job.stage === "closed") return null;
  if (sent) return "ready_to_close";
  if (job.stage === "complete") return "work_complete";
  if (job.sends_at || job.stage === "in_production") return "in_production";
  if (job.stage === "signed") return "signed";
  return null;
}

/**
 * Whether a job still belongs on its rep's pipeline.
 *
 * Off it once it is closed, lost, or sent to Kelsey. His words: "once it's
 * marked send to kelsey it's removed from the reps dashboard".
 */
export function onRepPipeline(
  job: { stage: JobStage },
  sent: boolean,
): boolean {
  return job.stage !== "lost" && job.stage !== "closed" && !sent;
}

/** Whole days since a moment, for "4 days" on a card. Never negative. */
export function daysSince(
  iso: string | null | undefined,
  now: Date,
): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}
